/**
 * An Ingestion step's parts: the editor for its fields (typed, or suggested
 * from a sample file read in the browser and then edited), whether it reads
 * one set of them or a row per item (AMO-299), and its template; on a run
 * the fields it read, a field it could not find flagged.
 *
 * The sample never leaves the browser as a file: its text is read here
 * (`extractDocumentText`, the contract reviews' reader, or as it is for CSV),
 * sent for suggestions, and dropped.
 *
 * @module components/workflows/IngestionFields
 */
import { WORKFLOW_LIMITS, type WorkflowNode, type WorkflowStepField } from "@amoreg/contracts";
import { FileUpIcon, PlusIcon, XIcon } from "lucide-react";
import { useLayoutEffect, useRef, useState } from "react";

import { suggestIngestionFields } from "~/state/workflows";
import { ACCEPTED_DOCUMENTS, extractDocumentText } from "../apps/contractIntelligence/extractDocumentText";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger } from "../ui/select";
import { Textarea } from "../ui/textarea";
import { mergeSuggestedFields, missingFieldNames } from "./workflowCanvas.logic";

type IngestionNode = Extract<WorkflowNode, { type: "ingestion" }>;

const ACCEPTED_SAMPLES = `${ACCEPTED_DOCUMENTS},.csv,text/csv`;

/** One set of fields for the whole material, or a row per item it lists (a table). */
const SHAPES = [
  { value: "fields", label: "One set of fields" },
  { value: "rows", label: "A row per item (a table)" },
] as const;

/** A sample file's text: CSV as it is, everything else through the document reader. */
async function sampleText(file: File): Promise<string> {
  if (file.name.toLowerCase().endsWith(".csv") || file.type === "text/csv") {
    if (file.size > 25 * 1024 * 1024) throw new Error("This file is larger than 25 MB.");
    return (await file.text()).trim();
  }
  return (await extractDocumentText(file)).text;
}

export function IngestionStepEditor({
  node,
  organizationId,
  onChange,
}: {
  readonly node: IngestionNode;
  /** Where suggestions are asked; null hides them. */
  readonly organizationId: string | null;
  readonly onChange: (next: IngestionNode) => void;
}) {
  const { fields } = node.config;
  const fileInput = useRef<HTMLInputElement>(null);
  // The step as it is now: a suggestion that comes back merges into the
  // fields as edited meanwhile, never the ones read when the file was chosen.
  const latest = useRef(node);
  useLayoutEffect(() => {
    latest.current = node;
  }, [node]);
  const [suggesting, setSuggesting] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const full = fields.length >= WORKFLOW_LIMITS.ingestionFields;
  const setFields = (next: IngestionNode["config"]["fields"]) => onChange({ ...node, config: { ...node.config, fields: [...next] } });

  const suggestFrom = async (file: File) => {
    if (!organizationId) return;
    const stepId = node.id;
    setSuggesting(true);
    setNotice(null);
    try {
      const text = await sampleText(file);
      if (text.length < 20) throw new Error("No text could be read from this file.");
      const outcome = await suggestIngestionFields(organizationId, {
        text: text.slice(0, WORKFLOW_LIMITS.sampleTextLength),
        fileName: file.name.slice(0, 255),
      });
      if (!outcome.ok) {
        setNotice(outcome.message);
        return;
      }
      const current = latest.current;
      // The panel moved to another step while this one was being read: leave that one alone.
      if (current.id !== stepId) return;
      const { fields: merged, added } = mergeSuggestedFields(current.config.fields, outcome.value);
      onChange({ ...current, config: { ...current.config, fields: [...merged] } });
      setNotice(
        added === 0
          ? "No new fields: the ones suggested are already listed."
          : `Added ${added} suggested field${added === 1 ? "" : "s"} from ${file.name}. Check and edit them before you run.`,
      );
    } catch (error) {
      setNotice(error instanceof Error ? error.message : "This file could not be read.");
    } finally {
      setSuggesting(false);
    }
  };

  const rows = node.config.shape === "rows";
  return (
    <>
      <label className="block space-y-1.5">
        <span className="text-xs font-medium">Read as</span>
        <Select
          value={rows ? "rows" : "fields"}
          onValueChange={(value) => onChange({ ...node, config: { ...node.config, shape: value === "rows" ? "rows" : "fields" } })}
        >
          <SelectTrigger size="sm" aria-label="Read as" data-workflow-ingestion-shape="">
            <span className="truncate text-xs">{SHAPES.find((choice) => choice.value === (rows ? "rows" : "fields"))?.label}</span>
          </SelectTrigger>
          <SelectContent>
            {SHAPES.map((choice) => (
              <SelectItem key={choice.value} value={choice.value}>
                {choice.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <span className="block text-[11px] leading-snug text-muted-foreground">
          {rows
            ? "Every item the material lists (each trade, holding or balance) becomes a row, with a column per field. The result is a table later steps can use."
            : "The fields are read once from the whole material."}
        </span>
      </label>
      <div className="space-y-1.5" data-workflow-ingestion-fields="">
        <span className="text-xs font-medium">{rows ? "Columns to read" : "Fields to read"}</span>
        {fields.length > 0 ? (
          <ul className="space-y-2">
            {fields.map((field, index) => (
              // oxlint-disable-next-line react/no-array-index-key -- Fields have no ids; their place is stable while editing.
              <li key={index} className="space-y-1 rounded-md bg-muted/40 p-2" data-workflow-ingestion-field={index}>
                <div className="flex items-center gap-1.5">
                  <Input
                    value={field.name}
                    maxLength={WORKFLOW_LIMITS.fieldNameLength}
                    placeholder="Field name, e.g. Client name"
                    aria-label={`Field ${index + 1} name`}
                    onChange={(event) =>
                      setFields(fields.map((entry, at) => (at === index ? { ...entry, name: event.currentTarget.value } : entry)))
                    }
                    data-workflow-field-name=""
                    data-step-field="config.fields"
                  />
                  <Button
                    size="icon-xs"
                    variant="ghost"
                    aria-label={`Remove ${field.name.trim() || `field ${index + 1}`}`}
                    onClick={() => setFields(fields.filter((_, at) => at !== index))}
                  >
                    <XIcon />
                  </Button>
                </div>
                <Input
                  value={field.description}
                  maxLength={WORKFLOW_LIMITS.fieldDescriptionLength}
                  placeholder="What it is (optional)"
                  aria-label={`Field ${index + 1} description`}
                  onChange={(event) =>
                    setFields(fields.map((entry, at) => (at === index ? { ...entry, description: event.currentTarget.value } : entry)))
                  }
                  className="text-xs"
                  data-workflow-field-description=""
                  data-step-field="config.fields"
                />
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-[12px] text-muted-foreground">No fields yet. Add them, or suggest them from a sample file.</p>
        )}
        <div className="flex flex-wrap gap-1.5">
          <Button
            size="sm"
            variant="outline"
            disabled={full}
            onClick={() => setFields([...fields, { name: "", description: "" }])}
            data-workflow-add-field=""
          >
            <PlusIcon />
            Add field
          </Button>
          {organizationId ? (
            <>
              <Button
                size="sm"
                variant="outline"
                disabled={suggesting || full}
                onClick={() => fileInput.current?.click()}
                data-workflow-suggest-fields=""
              >
                <FileUpIcon />
                {suggesting ? "Reading the sample…" : "Suggest from a sample"}
              </Button>
              <input
                ref={fileInput}
                type="file"
                accept={ACCEPTED_SAMPLES}
                className="hidden"
                data-workflow-sample-input=""
                onChange={(event) => {
                  const file = event.currentTarget.files?.[0];
                  event.currentTarget.value = "";
                  if (file) void suggestFrom(file);
                }}
              />
            </>
          ) : null}
        </div>
        {full ? (
          <p className="text-[11px] text-muted-foreground">That is the most one step can read ({WORKFLOW_LIMITS.ingestionFields}).</p>
        ) : null}
        {notice ? (
          <p className="text-[11px] leading-snug text-muted-foreground" role="status" data-workflow-suggest-notice="">
            {notice}
          </p>
        ) : null}
        <span className="block text-[11px] leading-snug text-muted-foreground">
          The sample (PDF, Word, text or CSV) is read in your browser and only its text is used to suggest fields. It is not kept.
        </span>
      </div>
      {rows ? null : (
        <label className="block space-y-1.5">
          <span className="text-xs font-medium">Template (optional)</span>
          <Textarea
            rows={6}
            value={node.config.template}
            maxLength={WORKFLOW_LIMITS.instructionsLength}
            placeholder={"e.g. Dear {{Client name}},\nYour policy {{Policy number}} renews on {{Renewal date}}."}
            onChange={(event) => onChange({ ...node, config: { ...node.config, template: event.currentTarget.value } })}
            data-workflow-ingestion-template=""
            data-step-field="config.template"
          />
          <span className="block text-[11px] leading-snug text-muted-foreground">
            With a template, the step's result is the template with each {"{{Field name}}"} filled in. Without one, it is the
            fields one per line. A field the AI cannot find is left empty and the run carries on.
          </span>
        </label>
      )}
    </>
  );
}

/** What an Ingestion step read on a run: each field, a field it could not find flagged. */
export function IngestionStepFields({ fields }: { readonly fields: ReadonlyArray<WorkflowStepField> }) {
  const missing = missingFieldNames(fields);
  return (
    <div className="space-y-1.5" data-workflow-step-fields="">
      <p className="text-xs font-medium">Fields</p>
      {missing.length > 0 ? (
        <p className="rounded-md bg-warning/10 px-2 py-1 text-[12px] text-warning-foreground" data-workflow-fields-missing="">
          {missing.length === 1 ? "1 field was" : `${missing.length} fields were`} not found: {missing.join(", ")}.
        </p>
      ) : null}
      <dl className="divide-y divide-border rounded-lg border border-border">
        {fields.map((field) => (
          <div key={field.name} className="grid grid-cols-[minmax(0,2fr)_minmax(0,3fr)] gap-2 px-2.5 py-1.5 text-[12.5px]">
            <dt className="truncate text-muted-foreground">{field.name}</dt>
            <dd className={field.value === null ? "italic text-muted-foreground" : "whitespace-pre-wrap break-words text-foreground"}>
              {field.value ?? "Not found"}
            </dd>
          </div>
        ))}
      </dl>
    </div>
  );
}
